import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, TrendingUp, ArrowUpRight } from 'lucide-react';
import { formatCurrency } from '../../utils/formatters';

interface SalesDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  salesData: {
    totalSales: number;
    invoiceSales: number;
    productionSales: number;
    sampleSales: number;
    expressSales: number;
    expressSampleSales: number;
  };
  period: string;
}

const SalesDetailModal: React.FC<SalesDetailModalProps> = ({
  isOpen,
  onClose,
  salesData,
  period
}) => {
  // Format period for display
  const getPeriodDisplay = (period: string) => {
    switch (period) {
      case 'month': return 'este mes';
      case 'month-1': return 'mes anterior';
      case 'month-2': return 'hace 2 meses';
      case 'month-3': return 'hace 3 meses';
      case 'month-4': return 'hace 4 meses';
      case 'quarter': return 'este trimestre';
      case 'year': return 'este año';
      case 'custom': return 'período seleccionado';
      default: return 'todo el tiempo';
    }
  };

  // Calculate percentages
  const getPercentage = (value: number) => {
    if (salesData.totalSales === 0) return 0;
    return Math.round((value / salesData.totalSales) * 100);
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50" />
        <Dialog.Content className="fixed left-[50%] top-[50%] max-h-[85vh] w-[90vw] max-w-[600px] translate-x-[-50%] translate-y-[-50%] rounded-lg bg-white dark:bg-gray-800 p-6 shadow-lg overflow-y-auto">
          <Dialog.Title className="text-xl font-semibold mb-4 text-gray-900 dark:text-white flex items-center">
            <TrendingUp className="mr-2 h-5 w-5 text-indigo-600 dark:text-indigo-400" />
            Detalle de Ventas ({getPeriodDisplay(period)})
          </Dialog.Title>
          
          <div className="space-y-6">
            {/* Total Sales */}
            <div className="bg-indigo-50 dark:bg-indigo-900/30 p-4 rounded-lg">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-medium text-indigo-700 dark:text-indigo-300">Ventas Totales</p>
                  <p className="text-2xl font-bold text-indigo-800 dark:text-indigo-200">{formatCurrency(salesData.totalSales)}</p>
                </div>
                <div className="p-3 bg-indigo-100 dark:bg-indigo-800 rounded-full">
                  <ArrowUpRight className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                </div>
              </div>
            </div>
            
            {/* Sales Breakdown */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Desglose de Ventas</h3>
              
              {/* Invoices */}
              <div className="bg-white dark:bg-gray-700 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Facturas</p>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">{formatCurrency(salesData.invoiceSales)}</p>
                  </div>
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {getPercentage(salesData.invoiceSales)}%
                  </div>
                </div>
                <div className="mt-2 w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                  <div 
                    className="bg-blue-600 dark:bg-blue-500 h-2 rounded-full" 
                    style={{ width: `${getPercentage(salesData.invoiceSales)}%` }}
                  ></div>
                </div>
              </div>
              
              {/* Express */}
              <div className="bg-white dark:bg-gray-700 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Express</p>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">{formatCurrency(salesData.expressSales)}</p>
                  </div>
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {getPercentage(salesData.expressSales)}%
                  </div>
                </div>
                <div className="mt-2 w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                  <div 
                    className="bg-orange-600 dark:bg-orange-500 h-2 rounded-full" 
                    style={{ width: `${getPercentage(salesData.expressSales)}%` }}
                  ></div>
                </div>
              </div>
              
              {/* Express Samples */}
              <div className="bg-white dark:bg-gray-700 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Muestras Express</p>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">{formatCurrency(salesData.expressSampleSales)}</p>
                  </div>
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {getPercentage(salesData.expressSampleSales)}%
                  </div>
                </div>
                <div className="mt-2 w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                  <div 
                    className="bg-pink-600 dark:bg-pink-500 h-2 rounded-full" 
                    style={{ width: `${getPercentage(salesData.expressSampleSales)}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {/* Samples Pro */}
              <div className="bg-white dark:bg-gray-700 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Muestras Pro</p>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">{formatCurrency(salesData.sampleSales)}</p>
                  </div>
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {getPercentage(salesData.sampleSales)}%
                  </div>
                </div>
                <div className="mt-2 w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                  <div 
                    className="bg-purple-600 dark:bg-purple-500 h-2 rounded-full" 
                    style={{ width: `${getPercentage(salesData.sampleSales)}%` }}
                  ></div>
                </div>
              </div>
          
          <div className="mt-6 flex justify-end">
            <button
              onClick={() => onClose()}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
            >
              Cerrar
            </button>
          </div>
          
          <Dialog.Close asChild>
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default SalesDetailModal;